/**
 * RegularVsSystemBoiler — Animated SVG comparing regular and system boiler circuits.
 * The only difference is the heating circuit pressure regime.
 */

export function RegularVsSystemBoiler() {
  return (
    <figure style={{ margin: 0 }}>
      <svg viewBox="0 0 520 280" width="100%" role="img" aria-label="Regular vs system boiler comparison">
        <title>Regular boiler (open-vented CH) vs system boiler (sealed CH)</title>
        <defs>
          <style>{`
            .rvs-pipe { stroke: #64748b; stroke-width: 2.5; fill: none; }
            .rvs-flow { stroke: #ef4444; stroke-width: 2; fill: none; opacity: 0.5; stroke-dasharray: 6,5; animation: rvs-dash 1.2s linear infinite; }
            .rvs-flow-blue { stroke: #3b82f6; stroke-width: 2; fill: none; opacity: 0.5; stroke-dasharray: 6,5; animation: rvs-dash 1.2s linear infinite; }
            .rvs-label { font-family: system-ui, sans-serif; font-size: 11px; fill: #475569; text-anchor: middle; }
            .rvs-heading { font-family: system-ui, sans-serif; font-size: 13px; fill: #1e293b; font-weight: 600; text-anchor: middle; }
            .rvs-component { fill: #f1f5f9; stroke: #475569; stroke-width: 1.5; }
            .rvs-divider { stroke: #cbd5e1; stroke-width: 1; stroke-dasharray: 4,4; }
            .rvs-same { font-family: system-ui, sans-serif; font-size: 10px; fill: #6366f1; text-anchor: middle; font-style: italic; }
            @keyframes rvs-dash { to { stroke-dashoffset: -11; } }
          `}</style>
        </defs>

        {/* Divider */}
        <line x1="260" y1="30" x2="260" y2="265" className="rvs-divider" />

        {/* === REGULAR (left) === */}
        <text x="130" y="20" className="rvs-heading">Regular boiler</text>

        {/* F&E tank */}
        <rect x="100" y="35" width="60" height="22" rx="3" className="rvs-component" />
        <text x="130" y="50" className="rvs-label" style={{ fontSize: '10px' }}>F&amp;E tank</text>

        {/* Vent pipe */}
        <line x1="130" y1="57" x2="130" y2="80" className="rvs-pipe" />

        {/* Boiler */}
        <rect x="90" y="80" width="80" height="35" rx="6" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" />
        <text x="130" y="102" className="rvs-label">Regular</text>

        {/* CH flow (open-vented) */}
        <line x1="90" y1="97" x2="50" y2="97" className="rvs-pipe" />
        <line x1="50" y1="97" x2="50" y2="150" className="rvs-pipe" />
        <line x1="50" y1="97" x2="50" y2="150" className="rvs-flow" />
        <rect x="30" y="150" width="40" height="25" rx="4" className="rvs-component" />
        <text x="50" y="167" className="rvs-label" style={{ fontSize: '9px' }}>Rads</text>
        <text x="50" y="192" className="rvs-label" style={{ fontSize: '9px', fill: '#f59e0b' }}>Open CH</text>

        {/* DHW side — cylinder (same for both) */}
        <line x1="170" y1="97" x2="210" y2="97" className="rvs-pipe" />
        <line x1="210" y1="97" x2="210" y2="140" className="rvs-pipe" />
        <line x1="210" y1="97" x2="210" y2="140" className="rvs-flow-blue" />
        <rect x="190" y="140" width="40" height="40" rx="6" className="rvs-component" />
        <text x="210" y="164" className="rvs-label" style={{ fontSize: '9px' }}>Cyl</text>
        <text x="210" y="198" className="rvs-same">Any DHW type</text>

        {/* === SYSTEM (right) === */}
        <text x="390" y="20" className="rvs-heading">System boiler</text>

        {/* Expansion vessel + gauge */}
        <ellipse cx="390" cy="48" rx="22" ry="12" className="rvs-component" />
        <text x="390" y="52" className="rvs-label" style={{ fontSize: '9px' }}>EV</text>
        <circle cx="345" cy="48" r="10" className="rvs-component" />
        <text x="345" y="52" className="rvs-label" style={{ fontSize: '8px' }}>bar</text>

        {/* Connection */}
        <line x1="370" y1="58" x2="390" y2="80" className="rvs-pipe" />

        {/* Boiler */}
        <rect x="350" y="80" width="80" height="35" rx="6" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" />
        <text x="390" y="102" className="rvs-label">System</text>

        {/* CH flow (sealed) */}
        <line x1="350" y1="97" x2="310" y2="97" className="rvs-pipe" />
        <line x1="310" y1="97" x2="310" y2="150" className="rvs-pipe" />
        <line x1="310" y1="97" x2="310" y2="150" className="rvs-flow-blue" />
        <rect x="290" y="150" width="40" height="25" rx="4" className="rvs-component" />
        <text x="310" y="167" className="rvs-label" style={{ fontSize: '9px' }}>Rads</text>
        <text x="310" y="192" className="rvs-label" style={{ fontSize: '9px', fill: '#3b82f6' }}>Sealed CH</text>

        {/* DHW side — cylinder (same for both) */}
        <line x1="430" y1="97" x2="470" y2="97" className="rvs-pipe" />
        <line x1="470" y1="97" x2="470" y2="140" className="rvs-pipe" />
        <line x1="470" y1="97" x2="470" y2="140" className="rvs-flow-blue" />
        <rect x="450" y="140" width="40" height="40" rx="6" className="rvs-component" />
        <text x="470" y="164" className="rvs-label" style={{ fontSize: '9px' }}>Cyl</text>
        <text x="470" y="198" className="rvs-same">Any DHW type</text>

        {/* Key difference callout */}
        <rect x="140" y="225" width="240" height="30" rx="6" fill="#ede9fe" stroke="#8b5cf6" strokeWidth="1" />
        <text x="260" y="244" className="rvs-label" style={{ fill: '#6d28d9', fontWeight: 600 }}>
          Only difference: CH circuit pressure
        </text>

        <text x="260" y="275" className="rvs-label" style={{ fontSize: '10px', fill: '#94a3b8' }}>
          Both can serve vented, unvented, or thermal store DHW
        </text>
      </svg>
    </figure>
  );
}
